import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useMobileContext } from '../components/Layout';
import { MobileHeader } from '../components/mobile';
import { 
  Plus, Search, Filter, CheckCircle, XCircle, Clock, 
  FileText, Building, Calendar, Euro, Upload, Trash2,
  Eye, ChevronDown, ChevronUp, History, Paperclip, X,
  Wrench, Zap, Droplet, Shield, Briefcase, HardHat, User, Folder
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Category icons mapping
const categoryIcons = {
  entretien: Wrench,
  reparations: Wrench,
  nettoyage: Wrench,
  energie: Zap,
  eau: Droplet,
  assurance: Shield,
  honoraires: Briefcase,
  travaux: HardHat,
  personnel: User,
  divers: Folder
};

const ExpenseApproval = () => {
  const { user, token } = useAuth();
  const { isMobile, onMenuClick } = useMobileContext();
  
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, total_approved_amount: 0 });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [buildingFilter, setBuildingFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [expandedExpenseId, setExpandedExpenseId] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    building_id: '',
    title: '',
    description: '',
    amount: '',
    category: 'divers',
    fournisseur: '',
    date_facture: '',
    numero_facture: '',
    attachments: []
  });
  
  const fileInputRef = useRef(null);
  
  // Check if user can approve
  const canApprove = ['gestionnaire', 'comptable', 'directeur_cabinet', 'admin', 'superadmin'].includes(user?.role);
  const canSubmit = ['gestionnaire', 'comptable', 'assistant', 'directeur_cabinet', 'admin', 'superadmin'].includes(user?.role);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build query params
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (categoryFilter) params.append('category', categoryFilter);
      if (buildingFilter) params.append('building_id', buildingFilter);
      
      const [expensesRes, categoriesRes, buildingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/expenses?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/expenses/categories`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/immeubles`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/expenses/stats${buildingFilter ? `?building_id=${buildingFilter}` : ''}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setExpenses(expensesRes.data.expenses || []);
      setCategories(categoriesRes.data.categories || []);
      setBuildings(buildingsRes.data || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement des données' });
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, categoryFilter, buildingFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateExpense = async (e) => {
    e.preventDefault();
    
    if (!formData.building_id || !formData.title || !formData.amount) {
      setMessage({ type: 'error', text: 'Veuillez remplir tous les champs obligatoires' });
      return;
    }
    
    try {
      await axios.post(`${API}/expenses`, {
        ...formData,
        amount: parseFloat(formData.amount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage({ type: 'success', text: 'Dépense soumise pour approbation' });
      setShowCreateModal(false);
      setFormData({
        building_id: '',
        title: '',
        description: '',
        amount: '',
        category: 'divers',
        fournisseur: '',
        date_facture: '',
        numero_facture: '',
        attachments: []
      });
      fetchData();
    } catch (error) {
      console.error('Error creating expense:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de la création' });
    }
  };

  const handleApprove = async (expenseId) => {
    try {
      await axios.post(`${API}/expenses/${expenseId}/approve`, {
        comment: 'Dépense approuvée'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage({ type: 'success', text: 'Dépense approuvée' });
      fetchData();
      if (showDetailModal) setShowDetailModal(false);
    } catch (error) {
      console.error('Error approving expense:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors de l\'approbation' });
    }
  };

  const handleReject = async (expenseId, reason = '') => {
    try {
      await axios.post(`${API}/expenses/${expenseId}/reject`, {
        reason: reason || 'Dépense rejetée'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage({ type: 'success', text: 'Dépense rejetée' });
      fetchData();
      if (showDetailModal) setShowDetailModal(false);
    } catch (error) {
      console.error('Error rejecting expense:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Erreur lors du rejet' });
    }
  };

  const handleViewDetail = async (expense) => {
    try {
      const res = await axios.get(`${API}/expenses/${expense.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedExpense(res.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error fetching expense detail:', error);
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    
    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'Fichier trop volumineux (max 5 Mo)' });
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, {
            name: file.name,
            type: file.type,
            data: reader.result
          }]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      approved: 'bg-green-100 text-green-800 border-green-200',
      rejected: 'bg-red-100 text-red-800 border-red-200'
    };
    const labels = {
      pending: 'En attente',
      approved: 'Approuvée',
      rejected: 'Rejetée'
    };
    const icons = {
      pending: Clock,
      approved: CheckCircle,
      rejected: XCircle
    };
    const Icon = icons[status] || Clock;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[status]}`}>
        <Icon size={12} className="mr-1" />
        {labels[status] || status}
      </span>
    );
  };

  const getCategoryIcon = (categoryId) => {
    const Icon = categoryIcons[categoryId] || Folder;
    return <Icon size={16} />;
  };

  const filteredExpenses = expenses.filter(expense => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      expense.title?.toLowerCase().includes(query) ||
      expense.fournisseur?.toLowerCase().includes(query) ||
      expense.numero_facture?.toLowerCase().includes(query)
    );
  });

  if (loading && !expenses.length) {
    return (
      <>
        {isMobile && <MobileHeader title="Approbation Dépenses" onMenuClick={onMenuClick} />}
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00D3A8]"></div>
        </div>
      </>
    );
  }

  return (
    <>
      {isMobile && <MobileHeader title="Approbation Dépenses" onMenuClick={onMenuClick} />}
      <div className={`space-y-4 md:space-y-6 ${isMobile ? 'p-4' : ''}`}>
        {/* Header */}
        {!isMobile && (
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-800">Approbation des Dépenses</h1>
            {canSubmit && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-[#00D3A8] hover:bg-[#00C7A1] text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                data-testid="create-expense-btn"
              >
                <Plus size={20} />
                <span>Nouvelle Dépense</span>
              </button>
            )}
          </div>
        )}

        {/* Message */}
        {message.text && (
          <div className={`p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
            'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {message.text}
            <button onClick={() => setMessage({ type: '', text: '' })} className="float-right">
              <X size={16} />
            </button>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-yellow-400">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">En attente</p>
                <p className="text-2xl font-bold text-gray-800">{stats.pending}</p>
              </div>
              <Clock className="text-yellow-400" size={32} />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-400">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Approuvées</p>
                <p className="text-2xl font-bold text-gray-800">{stats.approved}</p>
              </div>
              <CheckCircle className="text-green-400" size={32} />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-400">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Rejetées</p>
                <p className="text-2xl font-bold text-gray-800">{stats.rejected}</p>
              </div>
              <XCircle className="text-red-400" size={32} />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-[#00D3A8]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Approuvé</p>
                <p className="text-2xl font-bold text-gray-800">{stats.total_approved_amount?.toLocaleString('fr-FR')} €</p>
              </div>
              <Euro className="text-[#00D3A8]" size={32} />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                data-testid="search-expenses"
              />
            </div>
            
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
              data-testid="filter-status"
            >
              <option value="">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="approved">Approuvées</option>
              <option value="rejected">Rejetées</option>
            </select>
            
            {/* Category Filter */}
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
              data-testid="filter-category"
            >
              <option value="">Toutes les catégories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            
            {/* Building Filter */}
            <select
              value={buildingFilter}
              onChange={(e) => setBuildingFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
              data-testid="filter-building"
            >
              <option value="">Tous les immeubles</option>
              {buildings.map(b => (
                <option key={b.id} value={b.id}>{b.nom}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Mobile: Create Button */}
        {isMobile && canSubmit && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="w-full bg-[#00D3A8] hover:bg-[#00C7A1] text-white px-4 py-3 rounded-lg flex items-center justify-center space-x-2"
            data-testid="create-expense-btn-mobile"
          >
            <Plus size={20} />
            <span>Nouvelle Dépense</span>
          </button>
        )}

        {/* Expenses List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {filteredExpenses.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FileText size={48} className="mx-auto mb-4 opacity-50" />
              <p>Aucune dépense trouvée</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredExpenses.map(expense => (
                <div key={expense.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <span className="text-gray-400">{getCategoryIcon(expense.category)}</span>
                        <h3 className="font-semibold text-gray-800">{expense.title}</h3>
                        {getStatusBadge(expense.status)}
                      </div>
                      <div className="mt-2 text-sm text-gray-500 space-y-1">
                        <p className="flex items-center">
                          <Building size={14} className="mr-2" />
                          {expense.building_name || 'Immeuble non spécifié'}
                        </p>
                        <p className="flex items-center">
                          <Euro size={14} className="mr-2" />
                          <span className="font-semibold text-gray-800">{expense.amount?.toLocaleString('fr-FR')} €</span>
                        </p>
                        {expense.fournisseur && (
                          <p className="flex items-center">
                            <Briefcase size={14} className="mr-2" />
                            {expense.fournisseur}
                          </p>
                        )}
                        <p className="flex items-center">
                          <Calendar size={14} className="mr-2" />
                          Soumis le {new Date(expense.submitted_at).toLocaleDateString('fr-FR')}
                          {expense.submitter_name && ` par ${expense.submitter_name}`}
                        </p>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex flex-col space-y-2 ml-4">
                      <button
                        onClick={() => handleViewDetail(expense)}
                        className="text-gray-500 hover:text-[#00D3A8] p-2 rounded-lg hover:bg-gray-100"
                        title="Voir les détails"
                        data-testid={`view-expense-${expense.id}`}
                      >
                        <Eye size={18} />
                      </button>
                      
                      {expense.status === 'pending' && canApprove && expense.submitted_by !== user?.id && (
                        <>
                          <button
                            onClick={() => handleApprove(expense.id)}
                            className="text-green-500 hover:text-green-700 p-2 rounded-lg hover:bg-green-50"
                            title="Approuver"
                            data-testid={`approve-expense-${expense.id}`}
                          >
                            <CheckCircle size={18} />
                          </button>
                          <button
                            onClick={() => handleReject(expense.id)}
                            className="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50"
                            title="Rejeter"
                            data-testid={`reject-expense-${expense.id}`}
                          >
                            <XCircle size={18} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Attachments indicator */}
                  {expense.attachments?.length > 0 && (
                    <div className="mt-2 flex items-center text-sm text-gray-500">
                      <Paperclip size={14} className="mr-1" />
                      {expense.attachments.length} pièce(s) jointe(s)
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Create Expense Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-gray-800">Nouvelle Dépense</h2>
                  <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                    <X size={24} />
                  </button>
                </div>
              </div>
              
              <form onSubmit={handleCreateExpense} className="p-6 space-y-4">
                {/* Building */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Immeuble <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.building_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, building_id: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                    required
                    data-testid="expense-building"
                  >
                    <option value="">Sélectionner un immeuble</option>
                    {buildings.map(b => (
                      <option key={b.id} value={b.id}>{b.nom}</option>
                    ))}
                  </select>
                </div>
                
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Titre <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                    placeholder="Ex: Facture électricité Mars 2026"
                    required
                    data-testid="expense-title"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* Amount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Montant (€) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.amount}
                      onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                      placeholder="0.00"
                      required
                      data-testid="expense-amount"
                    />
                  </div>
                  
                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                      data-testid="expense-category"
                    >
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* Fournisseur */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fournisseur</label>
                    <input
                      type="text"
                      value={formData.fournisseur}
                      onChange={(e) => setFormData(prev => ({ ...prev, fournisseur: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                      placeholder="Ex: EDF, Engie..."
                      data-testid="expense-fournisseur"
                    />
                  </div>
                  
                  {/* Date facture */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date de facture</label>
                    <input
                      type="date"
                      value={formData.date_facture}
                      onChange={(e) => setFormData(prev => ({ ...prev, date_facture: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                      data-testid="expense-date"
                    />
                  </div>
                </div>
                
                {/* Numero facture */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Numéro de facture</label>
                  <input
                    type="text"
                    value={formData.numero_facture}
                    onChange={(e) => setFormData(prev => ({ ...prev, numero_facture: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                    placeholder="Ex: FAC-2026-001"
                    data-testid="expense-numero"
                  />
                </div>
                
                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent"
                    rows={3}
                    placeholder="Détails supplémentaires..."
                    data-testid="expense-description"
                  />
                </div>
                
                {/* Attachments */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pièces jointes</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full flex items-center justify-center space-x-2 text-gray-500 hover:text-[#00D3A8]"
                    >
                      <Upload size={20} />
                      <span>Ajouter des fichiers (PDF, JPG, PNG)</span>
                    </button>
                    
                    {formData.attachments.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {formData.attachments.map((file, index) => (
                          <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                            <span className="text-sm text-gray-600 truncate">{file.name}</span>
                            <button
                              type="button"
                              onClick={() => removeAttachment(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Submit */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="bg-[#00D3A8] hover:bg-[#00C7A1] text-white px-6 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                    data-testid="submit-expense"
                  >
                    <Plus size={20} />
                    <span>Soumettre</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Detail Modal */}
        {showDetailModal && selectedExpense && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">{selectedExpense.title}</h2>
                    <p className="text-sm text-gray-500 mt-1">
                      Soumis le {new Date(selectedExpense.submitted_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-gray-600">
                    <X size={24} />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Status */}
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Statut</span>
                  {getStatusBadge(selectedExpense.status)}
                </div>
                
                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Montant</p>
                    <p className="font-semibold text-lg">{selectedExpense.amount?.toLocaleString('fr-FR')} €</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Catégorie</p>
                    <p className="font-medium">{categories.find(c => c.id === selectedExpense.category)?.name || selectedExpense.category}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Immeuble</p>
                    <p className="font-medium">{selectedExpense.building?.nom || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Fournisseur</p>
                    <p className="font-medium">{selectedExpense.fournisseur || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Date facture</p>
                    <p className="font-medium">{selectedExpense.date_facture ? new Date(selectedExpense.date_facture).toLocaleDateString('fr-FR') : '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">N° facture</p>
                    <p className="font-medium">{selectedExpense.numero_facture || '-'}</p>
                  </div>
                </div>
                
                {/* Description */}
                {selectedExpense.description && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Description</p>
                    <p className="text-gray-700">{selectedExpense.description}</p>
                  </div>
                )}
                
                {/* Submitter */}
                <div>
                  <p className="text-sm text-gray-500 mb-1">Soumis par</p>
                  <p className="text-gray-700">
                    {selectedExpense.submitter?.prenom} {selectedExpense.submitter?.nom}
                    {selectedExpense.submitter?.email && (
                      <span className="text-gray-400 ml-2">({selectedExpense.submitter.email})</span>
                    )}
                  </p>
                </div>
                
                {/* Processor (if approved/rejected) */}
                {selectedExpense.processor && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">
                      {selectedExpense.status === 'approved' ? 'Approuvé par' : 'Rejeté par'}
                    </p>
                    <p className="text-gray-700">
                      {selectedExpense.processor.prenom} {selectedExpense.processor.nom}
                      {selectedExpense.processed_at && (
                        <span className="text-gray-400 ml-2">
                          le {new Date(selectedExpense.processed_at).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                    </p>
                  </div>
                )}
                
                {/* Rejection reason */}
                {selectedExpense.rejection_reason && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-red-800">Motif du rejet</p>
                    <p className="text-red-700 mt-1">{selectedExpense.rejection_reason}</p>
                  </div>
                )}
                
                {/* Attachments */}
                {selectedExpense.attachments?.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-500 mb-2 flex items-center">
                      <Paperclip size={14} className="mr-1" />
                      Pièces jointes ({selectedExpense.attachments.length})
                    </p>
                    <div className="space-y-2">
                      {selectedExpense.attachments.map((att, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          <span className="text-sm text-gray-600">{att.name}</span>
                          {att.data && (
                            <a
                              href={att.data}
                              download={att.name}
                              className="text-[#00D3A8] hover:text-[#00C7A1] text-sm"
                            >
                              Télécharger
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* History */}
                {selectedExpense.history?.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-500 mb-2 flex items-center">
                      <History size={14} className="mr-1" />
                      Historique
                    </p>
                    <div className="space-y-2">
                      {selectedExpense.history.map((entry, index) => (
                        <div key={index} className="flex items-start space-x-3 text-sm">
                          <div className={`w-2 h-2 rounded-full mt-1.5 ${
                            entry.action === 'approved' ? 'bg-green-500' :
                            entry.action === 'rejected' ? 'bg-red-500' :
                            'bg-gray-400'
                          }`} />
                          <div>
                            <p className="text-gray-700">{entry.comment}</p>
                            <p className="text-gray-400 text-xs">
                              {entry.user_name} - {new Date(entry.created_at).toLocaleString('fr-FR')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Actions */}
                {selectedExpense.status === 'pending' && canApprove && selectedExpense.submitted_by !== user?.id && (
                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => handleReject(selectedExpense.id)}
                      className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg flex items-center space-x-2 transition-colors"
                      data-testid="reject-expense-modal"
                    >
                      <XCircle size={20} />
                      <span>Rejeter</span>
                    </button>
                    <button
                      onClick={() => handleApprove(selectedExpense.id)}
                      className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                      data-testid="approve-expense-modal"
                    >
                      <CheckCircle size={20} />
                      <span>Approuver</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ExpenseApproval;
